import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../../api.js';
import Field from '../../components/Field.jsx';
import Alert from '../../components/ui/Alert.jsx';
import Modal, { ModalActions } from '../../components/ui/Modal.jsx';
import { EmptyState, Pill, Section } from '../../components/ui/index.jsx';
import { checkRequired } from '../../utils/validate.js';
import { ApprovedSendersField } from './approvedSenders.jsx';
import { fmtDay } from '../../utils/format.js';

function PlantModal({ base, eventId, data, onClose, onDone }) {
  const [form, setForm] = useState({ given_to: '', given_to_kind: 'exhibitor', allowed_domains: '', count: '3', note: '' });
  const [errors, setErrors] = useState({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const set = (k, v) => { setForm({ ...form, [k]: v }); if (errors[k]) setErrors({ ...errors, [k]: undefined }); };

  const save = async () => {
    const n = Number(form.count);
    const found = Object.fromEntries(Object.entries({
      given_to: checkRequired(form.given_to, 'who the list is going to', { max: 200 }),
      count: Number.isInteger(n) && n >= 1 && n <= data.max_contacts ? '' : `Between 1 and ${data.max_contacts}.`,
    }).filter(([, m]) => m));
    if (Object.keys(found).length) { setErrors(found); return; }
    setBusy(true); setError('');
    try {
      const res = await api(`${base}/plantings`, { method: 'POST', body: { ...form, count: n, event_id: eventId } });
      onDone(res.id);
    } catch (err) { setErrors(err.data?.fields || {}); setError(err.data?.fields ? '' : err.message); setBusy(false); }
  };

  return (
    <Modal title="Plant contacts in a list" onClose={onClose} onSubmit={save} busy={busy}
      description="We make up a few realistic people — names, job titles, companies, email addresses — that exist nowhere else. You add them to the list before you share it. If anyone emails them, you’ll know the list was used, and whether by the people you gave it to."
      footer={<ModalActions submitLabel="Make the contacts" busyLabel="Making…" busy={busy} onCancel={onClose} />}>
      {error && <Alert tone="danger">{error}</Alert>}
      <div className="row-2">
        <Field label="Who is the list going to?" required maxLength={200} value={form.given_to} onChange={(e) => set('given_to', e.target.value)}
          error={errors.given_to} placeholder="Acme Exhibits Ltd" />
        <Field label="They are" as="select" value={form.given_to_kind} onChange={(e) => set('given_to_kind', e.target.value)}>
          {Object.entries(data.kinds).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
        </Field>
      </div>
      <Alert tone="info">
        <strong>Best: one list per company you give it to.</strong> Then a leak points straight at who passed it on.
        Sending the same list to all your exhibitors? That works too: name it (e.g. “All exhibitors”) and paste every exhibitor’s domain below.
      </Alert>
      <ApprovedSendersField value={form.allowed_domains} onChange={(e) => set('allowed_domains', e.target.value)}
        error={errors.allowed_domains} max={data.max_approved_domains || 500} />
      <div className="row-2">
        <Field label="How many contacts" type="number" min={1} max={data.max_contacts} value={form.count} onChange={(e) => set('count', e.target.value)}
          error={errors.count} hint={!errors.count ? 'Two or three is usually enough. Scatter them through the list.' : undefined} />
        <Field label="Note" optional maxLength={500} value={form.note} onChange={(e) => set('note', e.target.value)} placeholder="Pre-show attendee list, sent 4 March" />
      </div>
    </Modal>
  );
}

/** An event's planted contacts: one row per list shared. Owners and admins only. */
export default function PlantingsSection({ organisationId, eventId }) {
  const base = `/portal/organisations/${organisationId}`;
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [planting, setPlanting] = useState(false);

  const load = useCallback(async () => {
    try { setData(await api(`${base}/plantings?event_id=${eventId}`)); } catch (err) { setError(err.message); }
  }, [base, eventId]);
  useEffect(() => { load(); }, [load]);

  // Lists the company shares with others (its own lists have their own section)
  const shared = (data?.plantings || []).filter((p) => p.purpose !== 'own_lists');
  const button = data?.available && <button type="button" className="btn btn-primary btn-small" onClick={() => setPlanting(true)}>Plant contacts in a list</button>;
  return (
    <Section title="Planted contacts"
      description="Sharing this event’s attendee list with an exhibitor, sponsor or partner? Hide a few made-up contacts in it first. Anyone who emails them got the list from that copy — so you’ll know if it’s been sold on or leaked."
      actions={button} flush={shared.length > 0}>
      {error && <Alert tone="danger">{error}</Alert>}
      {!data ? <p className="muted">Loading…</p> : shared.length === 0 ? (
        <EmptyState title="No lists shared yet" action={button}>
          {data.available ? 'Each list you share gets its own contacts, so a hit tells you exactly which copy was used.' : 'Planted contacts aren’t available yet. They will be soon.'}
        </EmptyState>
      ) : (
        <div className="table-scroll">
          <table className="table table-stack">
            <thead><tr><th>Given to</th><th>Contacts</th><th>Emails to them</th><th>Status</th></tr></thead>
            <tbody>
              {shared.map((p) => (
                <tr key={p.id}>
                  <td data-label="Given to">
                    <Link className="strong-link" to={`/events/${eventId}/plantings/${p.id}`}>{p.given_to}</Link>
                    <div className="muted small">{p.given_to_kind_label} · {fmtDay(p.created_at)}{p.note ? ` · ${p.note}` : ''}</div>
                  </td>
                  <td data-label="Contacts">{p.contacts}</td>
                  <td data-label="Emails to them">
                    {p.leaks > 0 ? <Pill tone="bad">{p.leaks} from someone else</Pill> : p.hits > 0 ? <Pill tone="info">{p.hits} expected</Pill> : <span className="muted">None yet</span>}
                    {p.approved_dropped > 0 && <div className="muted small">{p.approved_dropped} from approved senders, ignored</div>}
                  </td>
                  <td data-label="Status">{p.status === 'active' ? <Pill tone="good">Watching</Pill> : <Pill>Stopped</Pill>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {planting && <PlantModal base={base} eventId={eventId} data={data} onClose={() => setPlanting(false)}
        onDone={(id) => navigate(`/events/${eventId}/plantings/${id}`, { state: { created: true } })} />}
    </Section>
  );
}
